import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import Wordmark from "@/components/Wordmark";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useLocalePath, useT } from "@/i18n/LocaleContext";

/* Menu — Quantum US pattern (right-side overlay panel).
   Timing tuned to Phase 3 spec: 700ms open/close, 120ms link stagger. */
export const Header = () => {
  const { t } = useT();
  const localePath = useLocalePath();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const closeRef = useRef(null);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);

  const NAV = [
    { to: localePath("/"), label: t("menu.nav.home"), testid: "nav-home" },
    { to: localePath("/services"), label: t("menu.nav.services"), testid: "nav-services" },
    { to: localePath("/legal"), label: t("menu.nav.legal"), testid: "nav-legal" },
    { to: localePath("/contact"), label: t("menu.nav.contact"), testid: "nav-contact" },
  ];

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
      if (e.key === "Tab" && menuRef.current) {
        const focusables = menuRef.current.querySelectorAll(
          'a, button, [tabindex]:not([tabindex="-1"])'
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault(); last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault(); first.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    const t = window.setTimeout(() => closeRef.current?.focus(), 60);
    const trigger = triggerRef.current;

    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      window.clearTimeout(t);
      trigger?.focus();
    };
  }, [open]);

  return (
    <>
      <header
        data-testid="site-header"
        className="w-full bg-navy text-cream border-b border-cream/10 relative z-30"
      >
        <div className="mx-auto max-w-[1360px] px-6 md:px-10 lg:px-14 py-5 md:py-6 flex items-center justify-between gap-6">
          <Wordmark variant="light" />

          <button
            ref={triggerRef}
            type="button"
            onClick={() => setOpen(true)}
            data-testid="menu-trigger"
            aria-label={t("menu.open")}
            aria-expanded={open}
            aria-controls="site-menu-panel"
            className="group inline-flex items-center gap-3 font-sans uppercase text-cream/85 hover:text-cream transition-colors duration-500"
            style={{ fontSize: "11px", letterSpacing: "0.22em" }}
          >
            <span className="relative inline-block h-[9px] w-[26px]" aria-hidden>
              <span className="absolute left-0 top-0 h-px w-full bg-current" />
              <span className="absolute left-0 bottom-0 h-px w-full bg-current" />
            </span>
            {t("menu.open")}
          </button>
        </div>
      </header>

      <div
        aria-hidden={!open}
        className={[
          "fixed inset-0 z-[90] pointer-events-none transition-opacity duration-[600ms]",
          open ? "opacity-100 pointer-events-auto" : "opacity-0",
        ].join(" ")}
      >
        <button
          type="button"
          data-testid="menu-backdrop"
          onClick={() => setOpen(false)}
          aria-label={t("menu.close")}
          tabIndex={open ? 0 : -1}
          className={[
            "absolute inset-0 w-full h-full bg-navy/40 transition-[backdrop-filter,opacity] duration-[600ms]",
            open ? "backdrop-blur-md" : "backdrop-blur-none",
          ].join(" ")}
        />

        <aside
          ref={menuRef}
          id="site-menu-panel"
          data-testid="menu-panel"
          role="dialog"
          aria-modal="true"
          aria-label={t("menu.navigate")}
          className={[
            "absolute inset-y-0 right-0 z-[100] bg-navy/95 text-cream w-[88vw] md:w-[60vw] lg:w-[36vw] lg:max-w-[560px] lg:min-w-[480px]",
            "overflow-y-auto",
            "transform transition-transform duration-[900ms] will-change-transform",
            "motion-reduce:transition-none",
            open ? "translate-x-0" : "translate-x-full",
          ].join(" ")}
          style={{ transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)" }}
        >
          <div className="px-8 md:px-12 lg:px-14 pt-6 md:pt-7 pb-14 min-h-full flex flex-col">
            <div className="flex justify-end -mr-1">
              <button
                ref={closeRef}
                type="button"
                onClick={() => setOpen(false)}
                data-testid="menu-close"
                aria-label={t("menu.close")}
                className="inline-flex items-center gap-3 font-sans uppercase text-cream/85 hover:text-cream transition-colors duration-500"
                style={{ fontSize: "11px", letterSpacing: "0.22em" }}
              >
                <span className="relative inline-block h-[10px] w-[10px]" aria-hidden>
                  <span className="absolute left-1/2 top-1/2 h-px w-[14px] bg-current -translate-x-1/2 -translate-y-1/2 rotate-45" />
                  <span className="absolute left-1/2 top-1/2 h-px w-[14px] bg-current -translate-x-1/2 -translate-y-1/2 -rotate-45" />
                </span>
                {t("menu.close")}
              </button>
            </div>

            <div className="mt-12 md:mt-16">
              <div
                className="font-sans text-[10.5px] font-medium uppercase text-cream/45"
                style={{ letterSpacing: "0.28em" }}
              >
                {t("menu.navigate")}
              </div>
              <ul className="mt-6 md:mt-8 flex flex-col gap-2 md:gap-3 group/list">
                {NAV.map((item, i) => (
                  <li
                    key={item.to}
                    className={`opacity-0 ${open ? "animate-nav-in" : ""}`}
                    style={{ animationDelay: `${220 + i * 160}ms` }}
                  >
                    <NavLink
                      to={item.to}
                      end={item.to.split("#")[0].split("?")[0].endsWith(`/${item.to.split("/")[1]}`) && !item.to.includes("services") && !item.to.includes("legal") && !item.to.includes("contact")}
                      data-testid={`menu-${item.testid}`}
                      className={({ isActive }) =>
                        [
                          "group/link relative inline-flex items-center gap-4 font-serif text-cream leading-tight",
                          "text-[34px] md:text-[40px] transition-opacity duration-500",
                          "group-hover/list:opacity-30 hover:!opacity-100",
                          isActive ? "opacity-100" : "opacity-90",
                        ].join(" ")
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <span
                            aria-hidden
                            className={[
                              "h-px bg-gold transition-all duration-700",
                              isActive
                                ? "w-8 opacity-100"
                                : "w-0 opacity-0 group-hover/link:w-8 group-hover/link:opacity-100",
                            ].join(" ")}
                          />
                          <span className="nav-link-shift">{item.label}</span>
                        </>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-16 md:mt-24 pt-8 border-t border-cream/10">
              <div
                className="font-sans text-[10.5px] font-medium uppercase text-cream/45 mb-5"
                style={{ letterSpacing: "0.28em" }}
              >
                {t("menu.language")}
              </div>
              <LanguageSwitcher variant="light" />
              <p className="mt-4 font-sans italic text-[12.5px] text-cream/50 max-w-xs leading-relaxed">
                {t("menu.lang_note")}
              </p>
            </div>

            <div className="mt-auto pt-16 md:pt-24">
              <div className="pt-8 border-t border-cream/10">
                <div className="font-serif text-cream text-[15px]" style={{ letterSpacing: "0.02em" }}>
                  {t("menu.entity")}
                </div>
                <a
                  href="mailto:office@qcmlux.com"
                  className="mt-1 inline-block font-sans text-[12.5px] text-cream/70 hover:text-cream transition-colors duration-500"
                >
                  office@qcmlux.com
                </a>
                <a
                  href="tel:+35220420122"
                  className="mt-1 block font-sans text-[12.5px] text-cream/70 hover:text-cream transition-colors duration-500"
                >
                  +352 20 42 01 22
                </a>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </>
  );
};

export default Header;
